import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import { Fragment, useEffect, useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { TTS_LANDING_TEXT_KEY } from '@/components/speech/TTSLandingForm';
import { SELECTED_VOICE_STORAGE_KEY } from '@/components/speech/VoicesForLandingPage';
import YouTubeFacade, {
  PauseGlyph,
  PlayGlyph,
  TTS_SHOWCASE_VIDEO_ID,
  TTS_SHOWCASE_VIDEO_LENGTH,
  TTS_SHOWCASE_VIDEO_URL,
} from '@/components/speech/YouTubeFacade';

import {
  ActingDemoClip,
  COMPARISON_LINE,
  COMPARISON_NEW,
  COMPARISON_OLD,
  COMPARISON_VOICE_ID,
  COMPARISON_VOICE_IMAGE,
  COMPARISON_VOICE_NAME,
  MOOD_CLIPS,
  MOODS_LINE,
  MOODS_VOICE_IMAGE,
} from '@/constants/ttsActingDemos';
import { useAnalytics } from '@/contexts/Analytics';
import { getVoiceImageUrl } from '@/utils/voices';

export const VOICES_THAT_ACT_ANCHOR = 'voices-that-act';

type Tone = 'old' | 'new';

// One shared <audio> for every demo on the section, so starting a clip
// stops the one before it. Progress is read every frame while playing.
function useClipPlayer(onStart: (clip: ActingDemoClip) => void) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const stopTicking = () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  };

  const tick = () => {
    const audio = audioRef.current;
    if (audio && audio.duration) {
      setProgress(audio.currentTime / audio.duration);
    }
    frameRef.current = requestAnimationFrame(tick);
  };

  useEffect(
    () => () => {
      stopTicking();
      audioRef.current?.pause();
    },
    [],
  );

  const getAudio = () => {
    if (!audioRef.current) {
      const audio = new Audio();
      audio.preload = 'auto';
      audio.addEventListener('ended', () => {
        stopTicking();
        setPlaying(false);
        setProgress(0);
      });
      audioRef.current = audio;
    }
    return audioRef.current;
  };

  // Show the playing state at once; the network can take a moment to
  // deliver the first bytes, and a click with no reaction reads as broken.
  const start = (audio: HTMLAudioElement) => {
    setPlaying(true);
    stopTicking();
    frameRef.current = requestAnimationFrame(tick);
    audio.play().catch(() => {
      stopTicking();
      setPlaying(false);
    });
  };

  const toggle = (clip: ActingDemoClip) => {
    const audio = getAudio();
    if (activeId === clip.id) {
      if (playing) {
        audio.pause();
        stopTicking();
        setPlaying(false);
      } else {
        start(audio);
      }
      return;
    }
    audio.pause();
    stopTicking();
    audio.src = clip.src;
    setActiveId(clip.id);
    setProgress(0);
    onStart(clip);
    start(audio);
  };

  const stop = () => {
    audioRef.current?.pause();
    stopTicking();
    setPlaying(false);
  };

  return { activeId, playing, progress, toggle, stop };
}

// Half as many bars for the small mood rows, keeping the louder of each
// pair so the shape of the recording survives.
function halvePeaks(peaks: number[]) {
  const out: number[] = [];
  for (let i = 0; i < peaks.length; i += 2) {
    out.push(Math.max(peaks[i], peaks[i + 1] ?? peaks[i]));
  }
  return out;
}

function formatSeconds(seconds: number) {
  const rounded = Math.round(seconds);
  return `0:${String(rounded).padStart(2, '0')}`;
}

// A typed line with its [tags] shown the way you'd write them.
function TaggedLine({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\[[^\]\n]+\])/).map((part, index) =>
        /^\[[^\]]+\]$/.test(part) ? (
          <span
            key={index}
            className='mx-0.5 whitespace-nowrap rounded-md bg-[oklch(0.72_0.13_278/0.16)] px-1.5 py-0.5 align-[0.08em] font-mono text-[0.78em] text-[oklch(0.86_0.07_278)] ring-1 ring-inset ring-[oklch(0.78_0.1_278/0.3)]'
          >
            {part}
          </span>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        ),
      )}
    </>
  );
}

function WaveBars({
  peaks,
  className,
}: {
  peaks: number[];
  className: string;
}) {
  return (
    <div
      className={clsxm(
        'flex h-full w-full items-center gap-px sm:gap-[2px]',
        className,
      )}
    >
      {peaks.map((peak, index) => (
        <span
          key={index}
          className='min-w-0 flex-1 rounded-full bg-current'
          style={{ height: `${Math.max(10, peak)}%` }}
        />
      ))}
    </div>
  );
}

// The clip's real loudness envelope; the played part fills with colour.
function Waveform({
  peaks,
  progress,
  active,
  tone,
  className,
}: {
  peaks: number[];
  progress: number;
  active: boolean;
  tone: Tone;
  className?: string;
}) {
  return (
    <div
      className={clsxm('relative min-w-0 flex-1', className)}
      aria-hidden='true'
    >
      <WaveBars
        peaks={peaks}
        className={
          active
            ? 'text-[oklch(1_0_0/0.14)]'
            : 'text-[oklch(1_0_0/0.22)] transition-colors group-hover:text-[oklch(1_0_0/0.34)]'
        }
      />
      {active && (
        <div
          className='absolute inset-0'
          style={{ clipPath: `inset(0 ${(1 - progress) * 100}% 0 0)` }}
        >
          <WaveBars
            peaks={peaks}
            className={
              tone === 'old'
                ? 'text-[oklch(0.74_0.13_22)]'
                : 'text-[oklch(0.76_0.13_278)]'
            }
          />
        </div>
      )}
    </div>
  );
}

function PlayButtonFace({
  active,
  playing,
  tone,
  size = 'lg',
}: {
  active: boolean;
  playing: boolean;
  tone: Tone;
  size?: 'lg' | 'sm';
}) {
  const isOn = active && playing;
  return (
    <span
      className={clsxm(
        'flex flex-none items-center justify-center rounded-full transition-colors duration-200',
        size === 'lg' ? 'h-11 w-11' : 'h-9 w-9',
        isOn
          ? tone === 'old'
            ? 'bg-[oklch(0.74_0.13_22)] text-[oklch(0.2_0.04_22)]'
            : 'bg-[oklch(0.76_0.13_278)] text-[oklch(0.2_0.05_278)]'
          : 'bg-[oklch(1_0_0/0.08)] text-white ring-1 ring-inset ring-[oklch(1_0_0/0.14)] group-hover:bg-[oklch(1_0_0/0.14)]',
      )}
    >
      {isOn ? (
        <PauseGlyph className={size === 'lg' ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
      ) : (
        <PlayGlyph
          className={clsxm('ml-0.5', size === 'lg' ? 'h-4 w-4' : 'h-3.5 w-3.5')}
        />
      )}
    </span>
  );
}

/**
 * "Voices that act" landing section: the showcase video, the same line read
 * by an older model and by ours, and one line in six moods. Every clip is a
 * real recording from our text to speech, so visitors hear the difference
 * without signing up.
 */
export default function ActingVoicesSection() {
  const { t } = useLocale('text-to-speech');
  const analytics = useAnalytics();
  const [videoPlaying, setVideoPlaying] = useState(false);
  // A demo clip and the video never play at once: starting a clip closes
  // the player, and playing the video stops the clip.
  const player = useClipPlayer((clip) => {
    setVideoPlaying(false);
    analytics.track('TTS Landing Demo Played', { clip: clip.id });
  });

  const takes: {
    clip: ActingDemoClip;
    tone: Tone;
    label: string;
    note: string;
  }[] = [
    {
      clip: COMPARISON_OLD,
      tone: 'old',
      label: t('Older AI voice'),
      note: t('Reads the tag out loud'),
    },
    {
      clip: COMPARISON_NEW,
      tone: 'new',
      label: 'Easy-Peasy.AI',
      note: t('Laughs on cue'),
    },
  ];

  const tryAudioTags = () => {
    analytics.track('TTS Landing Try Tags Clicked');
    try {
      localStorage.setItem(
        TTS_LANDING_TEXT_KEY,
        JSON.stringify(COMPARISON_LINE),
      );
      localStorage.setItem(
        SELECTED_VOICE_STORAGE_KEY,
        JSON.stringify({
          name: COMPARISON_VOICE_NAME,
          voice_id: COMPARISON_VOICE_ID,
        }),
      );
    } catch {
      // Storage blocked: /tts still opens, just without the example.
    }
    window.location.href = '/tts';
  };

  const playVideo = () => {
    player.stop();
    setVideoPlaying(true);
    analytics.track('TTS Landing Video Played');
  };

  return (
    <section
      id={VOICES_THAT_ACT_ANCHOR}
      aria-labelledby={`${VOICES_THAT_ACT_ANCHOR}-title`}
      className='relative isolate scroll-mt-16 overflow-hidden bg-[oklch(0.17_0.035_278)] py-24 text-white dark:border-y dark:border-[oklch(1_0_0/0.06)] sm:py-32'
    >
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16'>
          <div className='min-w-0 lg:col-span-5'>
            <p className='flex items-center gap-2.5 text-sm font-medium text-[oklch(0.84_0.07_278)]'>
              <span className='rounded-full bg-[oklch(0.72_0.13_278/0.18)] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[oklch(0.9_0.05_278)]'>
                {t('New')}
              </span>
              {t('Voices that act')}
            </p>
            <h2
              id={`${VOICES_THAT_ACT_ANCHOR}-title`}
              className='mt-5 text-4xl font-semibold leading-[1.06] tracking-tight [hyphens:auto] [overflow-wrap:anywhere] [text-wrap:balance] text-[oklch(0.98_0.005_278)] sm:text-5xl lg:text-[3.4rem]'
            >
              {t("It doesn't read your notes.")}
              <span className='block text-[oklch(0.8_0.11_278)]'>
                {t('It acts them.')}
              </span>
            </h2>
            <p className='mt-6 max-w-xl text-lg leading-8 text-[oklch(0.8_0.02_278)]'>
              <TaggedLine
                text={t(
                  'Put a direction in square brackets, like [laughs] or [whispers], and the voice performs it instead of saying the word.',
                )}
              />
            </p>
            <div className='mt-9 flex flex-wrap items-center gap-x-6 gap-y-4'>
              <button
                type='button'
                onClick={tryAudioTags}
                className='inline-flex items-center gap-2 rounded-full bg-[oklch(0.985_0.005_278)] px-6 py-3 text-sm font-semibold text-[oklch(0.24_0.06_278)] shadow-sm transition-[transform,background-color] duration-200 ease-out hover:bg-white active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-[oklch(0.8_0.11_278)] focus-visible:ring-offset-2 focus-visible:ring-offset-[oklch(0.17_0.035_278)]'
              >
                {t('Try audio tags free')}
                <span aria-hidden='true'>&rarr;</span>
              </button>
              <a
                href={TTS_SHOWCASE_VIDEO_URL}
                target='_blank'
                rel='noopener noreferrer'
                onClick={() => analytics.track('TTS Landing YouTube Clicked')}
                className='text-sm font-medium text-[oklch(0.8_0.02_278)] underline decoration-[oklch(1_0_0/0.25)] underline-offset-4 transition-colors hover:text-white hover:decoration-white'
              >
                {t('Watch on YouTube')}
              </a>
            </div>
          </div>

          <figure className='min-w-0 lg:col-span-7'>
            <div className='overflow-hidden rounded-2xl shadow-[0_40px_90px_-30px_oklch(0.08_0.05_278/0.9)] ring-1 ring-[oklch(1_0_0/0.1)]'>
              <YouTubeFacade
                videoId={TTS_SHOWCASE_VIDEO_ID}
                title={t('Voices that act')}
                length={TTS_SHOWCASE_VIDEO_LENGTH}
                playing={videoPlaying}
                onPlay={playVideo}
                playLabel={t('Play video')}
              />
            </div>
            <figcaption className='mt-3 text-sm text-[oklch(0.7_0.02_278)]'>
              {t('Every voice in this video is text to speech.')}
            </figcaption>
          </figure>
        </div>

        <div className='mt-20 grid grid-cols-1 gap-6 lg:mt-28 lg:grid-cols-12 lg:gap-8'>
          {/* Same voice, same line: before and after */}
          <div className='flex min-w-0 flex-col rounded-3xl bg-[oklch(1_0_0/0.035)] p-5 ring-1 ring-inset ring-[oklch(1_0_0/0.08)] sm:p-8 lg:col-span-7'>
            <div className='flex items-center gap-3'>
              <Image
                src={getVoiceImageUrl(COMPARISON_VOICE_IMAGE)}
                alt=''
                width={40}
                height={40}
                className='h-10 w-10 rounded-full object-cover ring-2 ring-[oklch(1_0_0/0.1)]'
              />
              <div>
                <p className='text-sm font-semibold text-white'>
                  {t('Same voice, same line')}
                </p>
                <p className='text-xs text-[oklch(0.7_0.02_278)]'>Matt</p>
              </div>
            </div>
            <p className='mt-6 text-xl leading-relaxed text-[oklch(0.93_0.01_278)] sm:text-2xl sm:leading-relaxed'>
              &ldquo;
              <TaggedLine text={COMPARISON_LINE} />
              &rdquo;
            </p>
            <div className='mt-6 divide-y divide-[oklch(1_0_0/0.07)] border-t border-[oklch(1_0_0/0.07)]'>
              {takes.map(({ clip, tone, label, note }) => {
                const active = player.activeId === clip.id;
                return (
                  <button
                    key={clip.id}
                    type='button'
                    onClick={() => player.toggle(clip)}
                    aria-pressed={active && player.playing}
                    aria-label={`${active && player.playing ? t('Pause') : t('Play')}: ${label}`}
                    className='group -mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-xl px-2 py-4 text-left sm:gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[oklch(0.8_0.11_278)]'
                  >
                    <PlayButtonFace
                      active={active}
                      playing={player.playing}
                      tone={tone}
                    />
                    <span className='w-28 flex-none sm:w-40'>
                      <span className='block text-sm font-semibold text-white'>
                        {label}
                      </span>
                      <span
                        className={clsxm(
                          'mt-0.5 block text-xs',
                          tone === 'old'
                            ? 'text-[oklch(0.78_0.1_22)]'
                            : 'text-[oklch(0.82_0.09_278)]',
                        )}
                      >
                        {note}
                      </span>
                    </span>
                    <Waveform
                      peaks={clip.peaks}
                      progress={active ? player.progress : 0}
                      active={active}
                      tone={tone}
                      className='h-9'
                    />
                    <span className='w-9 flex-none text-right text-xs tabular-nums text-[oklch(0.65_0.02_278)]'>
                      {formatSeconds(clip.duration)}
                    </span>
                  </button>
                );
              })}
            </div>
            <button
              type='button'
              onClick={tryAudioTags}
              className='group mt-auto inline-flex items-center gap-1.5 self-start pt-6 text-sm font-semibold text-[oklch(0.84_0.08_278)] transition-colors hover:text-white focus:outline-none focus-visible:underline'
            >
              {t('Try this line yourself')}
              <span
                aria-hidden='true'
                className='transition-transform duration-200 ease-out group-hover:translate-x-0.5'
              >
                &rarr;
              </span>
            </button>
          </div>

          {/* One line, six moods */}
          <div className='min-w-0 rounded-3xl bg-[oklch(1_0_0/0.035)] p-5 ring-1 ring-inset ring-[oklch(1_0_0/0.08)] sm:p-8 lg:col-span-5'>
            <div className='flex items-center gap-3'>
              <Image
                src={getVoiceImageUrl(MOODS_VOICE_IMAGE)}
                alt=''
                width={40}
                height={40}
                className='h-10 w-10 rounded-full object-cover ring-2 ring-[oklch(1_0_0/0.1)]'
              />
              <div>
                <p className='text-sm font-semibold text-white'>
                  {t('One line, six moods')}
                </p>
                <p className='text-xs text-[oklch(0.7_0.02_278)]'>Lisa</p>
              </div>
            </div>
            <p className='mt-6 text-xl leading-relaxed text-[oklch(0.93_0.01_278)] sm:text-2xl sm:leading-relaxed'>
              &ldquo;{MOODS_LINE}&rdquo;
            </p>
            <ul className='mt-5 space-y-1'>
              {MOOD_CLIPS.map((clip) => {
                const active = player.activeId === clip.id;
                return (
                  <li key={clip.id}>
                    <button
                      type='button'
                      onClick={() => player.toggle(clip)}
                      aria-pressed={active && player.playing}
                      aria-label={`${active && player.playing ? t('Pause') : t('Play')}: ${clip.tag}`}
                      className={clsxm(
                        'group -mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[oklch(0.8_0.11_278)]',
                        active
                          ? 'bg-[oklch(1_0_0/0.05)]'
                          : 'hover:bg-[oklch(1_0_0/0.03)]',
                      )}
                    >
                      <PlayButtonFace
                        active={active}
                        playing={player.playing}
                        tone='new'
                        size='sm'
                      />
                      <span className='min-w-0 flex-none basis-[42%] truncate text-sm sm:basis-[45%] sm:text-[0.95rem]'>
                        <TaggedLine text={clip.tag} />
                      </span>
                      <Waveform
                        peaks={halvePeaks(clip.peaks)}
                        progress={active ? player.progress : 0}
                        active={active}
                        tone='new'
                        className='h-6'
                      />
                      <span className='w-8 flex-none text-right text-xs tabular-nums text-[oklch(0.65_0.02_278)]'>
                        {formatSeconds(clip.duration)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
